// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { webSocketMessageError } from '../../../web-utils/src/store/Websocket/WebsocketSlice';
import { wsModelRouter } from './WebsocketModelRoutes';
import {
  sceneInStudioChanged,
  studioConditionChanged,
  studioStateChanged,
} from '../studio/studioSlice';
import { wsUpdateEquipment, wsPresetManagerUpdate } from '../equipment/equipmentSlice';
import { showRundownLaunchChanged } from '../shows/showsSlice';
import { setUserMessageError } from '../user/userSlice';

/**
 * WebSocket Event Handler Registry
 *
 * Maps WebSocket `event` names to handler functions.
 * Each handler is invoked with:
 *
 * @param {Object} data     Parsed payload from the WebSocket message
 * @param {Function} dispatch Redux dispatch function
 */

const wsEventHandlers = {
  /**
   * generic error message handler for WebSocket messages
   */
  Error: (data, dispatch) => dispatch(webSocketMessageError(data?.detail)),
  /**
   * User-related informational or error messages
   * (e.g. authentication, licensing, access issues).
   */
  UserMessage: (data, dispatch) => {
    dispatch(setUserMessageError({ type: data?.type, message: data?.message }));
  },
  /**
   * Studio state updates
   * studioState : Connected, Disconnected.
   */
  StudioStateChanged: (data, dispatch) =>
    dispatch(
      studioStateChanged({
        studioUUID: data?.studioUUID,
        studioState: data?.functional_state,
      })
    ),
  /**
   * Studio condition updates
   * studioCondition : On air,In rehearsal, Off air,Unknown.
   */
  StudioConditionChanged: (data, dispatch) =>
    dispatch(
      studioConditionChanged({
        studioUUID: data?.studioUUID,
        studioCondition: data?.studioCondition,
      })
    ),
  /**
   * Scene state updates within a Studio
   * status: undownloaded, downloading, downloaded, failed downloading, loaded, loading, failed loading.
   * progressPercentage (optional).
   */
  SceneInStudioCurrentStateChanged: (data, dispatch) =>
    dispatch(
      sceneInStudioChanged({
        studioUUID: data?.studioUUID,
        sceneUUID: data?.sceneUUID,
        status: data?.state,
        downloadPercentage: data?.progressPercentage ?? 0,
      })
    ),
  /**
   * Show deployment / rundown launch updates within Studio
   * state: launching, launched, failed launching.
   * progressPercentage (optional).
   * message (optional > only on failed launching).
   */
  ShowStudioRundownLaunchState: (data, dispatch) =>
    dispatch(
      showRundownLaunchChanged({
        deploymentUUID: data?.deploymentUUID,
        state: data?.state,
        progressPercentage: data?.progressPercentage ?? 0,
        message: data?.message ?? '',
      })
    ),
  /**
   * StudioEquipmentStateChanged
   * ----------------------------
   * Triggered when the backend notifies that
   * an equipment state has changed.
   *
   * Expected WebSocket message format:
   * {
   *   body: {
   *     event: "StudioEquipmentStateChanged",
   *     data: { ... }
   *   }
   * }
   *
   * `data` contains the updated equipment properties/settings.
   */
  EquipmentInStudioStateChanged: (data, dispatch) => {
    dispatch(wsUpdateEquipment(data));
  },
  // TODO : remove this event handler once backend rename for the above
  EquipmentInStudioStateChangedFromApi: (data, dispatch) => {
    dispatch(wsUpdateEquipment(data));
  },
  StudioEquipmentStateChanged: (data, dispatch) => {
    dispatch(wsUpdateEquipment(data));
  },
  EquipmentInStudioDesiredStateFromApi: (data, dispatch) => {
    dispatch(wsUpdateEquipment(data));
    // Preset manager send JSON in settings when preset/sequence is applied, stopped
    dispatch(wsPresetManagerUpdate(data));
  },
  /**
   * Generic model change dispatcher.
   *
   * Routes Create / Update / Delete events to the appropriate model/state reducer defined in WebsocketModelRoute.jsx.
   */
  ModelDataChanged: (data, dispatch) => {
    const { model_name: modelName, action, model_data: modelData } = data;
    const modelHandler = wsModelRouter[modelName]?.[action];
    if (modelHandler) {
      try {
        modelHandler(dispatch, modelData);
      } catch (err) {
        dispatch(webSocketMessageError(`Failed handling ${modelName}.${action}`));
      }
    }
  },
};

export default wsEventHandlers;
